import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback, useMemo, useState, type ChangeEvent } from "react";
import {
  BENCH_STEP_OPTIONS,
  skipsRouting,
  sortStepOptions,
  stepStopLabel,
  type BenchStepOption,
  type BenchStepSortColumn,
  type BenchStepSortDirection,
} from "../bench-steps";

const styles = {
  header: {
    display: "flex",
    gap: 2,
    alignItems: "center",
    flexWrap: "wrap",
  } satisfies SxProps<Theme>,
  muted: { color: "text.secondary" } satisfies SxProps<Theme>,
  table: { maxHeight: 440 } satisfies SxProps<Theme>,
  skippedRow: { opacity: 0.55 } satisfies SxProps<Theme>,
  orderCell: { width: 160, whiteSpace: "nowrap" } satisfies SxProps<Theme>,
  stopCell: { width: 110 } satisfies SxProps<Theme>,
  stepCell: { minWidth: 260, whiteSpace: "nowrap" } satisfies SxProps<Theme>,
};

const COLUMNS: ReadonlyArray<{
  column: BenchStepSortColumn;
  label: string;
  sx?: SxProps<Theme>;
}> = [
  { column: "order", label: "Sequence #", sx: styles.orderCell },
  { column: "label", label: "Step", sx: styles.stepCell },
  { column: "stop", label: "Stop", sx: styles.stopCell },
  { column: "sends", label: "Sends" },
];

type StepRowProps = {
  readonly step: BenchStepOption;
  readonly included: boolean;
  readonly onToggle: (key: string, included: boolean) => void;
};

const StepRow = memo(({ step, included, onToggle }: StepRowProps) => {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onToggle(step.key, event.target.checked);
    },
    [onToggle, step.key],
  );
  const inputProps = useMemo(
    () => ({ "aria-label": `Run ${step.label}` }),
    [step.label],
  );
  return (
    <TableRow hover sx={included ? undefined : styles.skippedRow}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={included}
          inputProps={inputProps}
          onChange={handleChange}
        />
      </TableCell>
      <TableCell>{step.order}</TableCell>
      <TableCell sx={styles.stepCell}>{step.label}</TableCell>
      <TableCell>{stepStopLabel(step.stopRole)}</TableCell>
      <TableCell sx={styles.muted}>{step.sends}</TableCell>
    </TableRow>
  );
});
StepRow.displayName = "StepRow";

type SortHeaderProps = {
  readonly column: BenchStepSortColumn;
  readonly label: string;
  readonly sx?: SxProps<Theme>;
  readonly activeColumn: BenchStepSortColumn;
  readonly direction: BenchStepSortDirection;
  readonly onSort: (column: BenchStepSortColumn) => void;
};

const SortHeader = memo(
  ({ column, label, sx, activeColumn, direction, onSort }: SortHeaderProps) => {
    const active = column === activeColumn;
    const handleClick = useCallback(() => {
      onSort(column);
    }, [column, onSort]);
    return (
      <TableCell sortDirection={active ? direction : false} sx={sx}>
        <TableSortLabel
          active={active}
          direction={active ? direction : "asc"}
          onClick={handleClick}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  },
);
SortHeader.displayName = "SortHeader";

const SELECT_ALL_INPUT_PROPS = { "aria-label": "Run every step" };

type StepSelectionSectionProps = {
  readonly skippedKeys: ReadonlySet<string>;
  readonly onChange: (skippedKeys: ReadonlySet<string>) => void;
  readonly send990ForLiveOrders: boolean;
  readonly onSend990ForLiveOrdersChange: (send: boolean) => void;
};

/**
 * Which lifecycle steps a run takes. Every step is on until the operator
 * switches it off; the plan then reports the step SKIPPED and the run moves
 * on to the next one. The table sorts by step name until the operator picks
 * another column; "Sequence #" restores the order a run takes the steps in.
 *
 * "Send 990 ACCEPTED for live orders" lets Accept tender send the 990 for an
 * order that arrived live. A live order has no draft to approve, so without it
 * the run sends no 990 for a partnership that does not auto-accept.
 */
const StepSelectionSection = ({
  skippedKeys,
  onChange,
  send990ForLiveOrders,
  onSend990ForLiveOrdersChange,
}: StepSelectionSectionProps) => {
  const [sortColumn, setSortColumn] = useState<BenchStepSortColumn>("label");
  const [sortDirection, setSortDirection] =
    useState<BenchStepSortDirection>("asc");

  const sortedSteps = useMemo(
    () => sortStepOptions(BENCH_STEP_OPTIONS, sortColumn, sortDirection),
    [sortColumn, sortDirection],
  );

  const handleSort = useCallback(
    (column: BenchStepSortColumn) => {
      if (column === sortColumn) {
        setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
        return;
      }
      setSortColumn(column);
      setSortDirection("asc");
    },
    [sortColumn],
  );

  const handleToggle = useCallback(
    (key: string, included: boolean) => {
      const next = new Set(skippedKeys);
      if (included) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onChange(next);
    },
    [onChange, skippedKeys],
  );
  const handleRunEvery = useCallback(() => {
    onChange(new Set());
  }, [onChange]);
  const handleToggleAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(
        event.target.checked
          ? new Set()
          : new Set(BENCH_STEP_OPTIONS.map((step) => step.key)),
      );
    },
    [onChange],
  );

  const handleSend990Change = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSend990ForLiveOrdersChange(event.target.checked);
    },
    [onSend990ForLiveOrdersChange],
  );

  const noneSkipped = skippedKeys.size === 0;
  const allSkipped = skippedKeys.size === BENCH_STEP_OPTIONS.length;

  return (
    <Stack spacing={1}>
      <Box sx={styles.header}>
        <Typography variant="subtitle2">Steps to run</Typography>
        <Button disabled={noneSkipped} size="small" onClick={handleRunEvery}>
          Run every step
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={send990ForLiveOrders}
              onChange={handleSend990Change}
            />
          }
          label="Send 990 ACCEPTED for live orders"
        />
      </Box>
      <TableContainer sx={styles.table}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={noneSkipped}
                  indeterminate={!noneSkipped && !allSkipped}
                  inputProps={SELECT_ALL_INPUT_PROPS}
                  onChange={handleToggleAll}
                />
              </TableCell>
              {COLUMNS.map(({ column, label, sx }) => (
                <SortHeader
                  key={column}
                  activeColumn={sortColumn}
                  column={column}
                  direction={sortDirection}
                  label={label}
                  sx={sx}
                  onSort={handleSort}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSteps.map((step) => (
              <StepRow
                key={step.key}
                included={!skippedKeys.has(step.key)}
                step={step}
                onToggle={handleToggle}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!noneSkipped && (
        <Alert severity="info">
          A skipped step does not happen in the TMS either, so a later step can
          fail when it needs it: completing a stop that never arrived, or
          posting an invoice the order was never added to.
          {skipsRouting(skippedKeys)
            ? " Arriving at a stop with no route fails, so leave its route step on unless the stop is already routed."
            : ""}
        </Alert>
      )}
    </Stack>
  );
};

export default StepSelectionSection;
